import * as React from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useAccount } from "@/hooks/useAccount";
import { useUpdateProfile } from "@/hooks/useUpdateProfile";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";

/**
 * Self-service profile editor — company name + phone for now.
 * Email + password edits live in their own (future) flows because each
 * touches the Identity service plus verification email round-trips.
 */
export function ProfileForm() {
  const { data, isLoading } = useAccount();
  const update = useUpdateProfile();
  const { toast } = useToast();

  const [companyName, setCompanyName] = React.useState("");
  const [phone, setPhone]             = React.useState("");
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string[]>>({});

  // Seed local state from server only once (or when account ID changes).
  // Re-syncing on every refetch would clobber an in-flight edit.
  const seededFor = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (data && seededFor.current !== data.id) {
      seededFor.current = data.id;
      setCompanyName(data.company_name ?? "");
      setPhone(data.phone ?? "");
    }
  }, [data]);

  if (isLoading) return <FormSkeleton />;
  if (!data) return null;

  const dirty = (companyName ?? "") !== (data.company_name ?? "") ||
                (phone       ?? "") !== (data.phone       ?? "");

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});
    try {
      await update.mutateAsync({
        company_name: companyName.trim() || null,
        phone:        phone.trim()       || null
      });
      toast({ title: "Profile saved", variant: "success" });
    } catch (err) {
      if (err instanceof ApiError && err.body && typeof err.body === "object") {
        const body = err.body as { errors?: Record<string, string[]>; message?: string };
        if (body.errors) setFieldErrors(body.errors);
        toast({
          title: "Couldn't save profile",
          description: body.message ?? err.message,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Couldn't save profile",
          description: err instanceof Error ? err.message : "Network error.",
          variant: "destructive"
        });
      }
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>
          The contact information attached to your HosterDash account.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-5">
          <ReadOnlyRow
            label="Email"
            value={maskedEmailFor(data)}
            hint="Email changes need to go through support for now."
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="companyName">Company name</Label>
              <Input
                id="companyName"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                placeholder="Acme Inc."
                aria-invalid={!!fieldErrors["Request.CompanyName"]}
              />
              {fieldErrors["Request.CompanyName"] && (
                <p className="text-xs text-destructive">{fieldErrors["Request.CompanyName"][0]}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="+1 415 555 0123"
                inputMode="tel"
                autoComplete="tel"
                aria-invalid={!!fieldErrors["Request.Phone"]}
              />
              {fieldErrors["Request.Phone"] && (
                <p className="text-xs text-destructive">{fieldErrors["Request.Phone"][0]}</p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">
              {dirty ? "You have unsaved changes." : "Everything is up to date."}
            </p>
            <Button type="submit" disabled={!dirty || update.isPending}>
              {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save profile
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

interface ReadOnlyRowProps {
  label: string;
  value: string;
  hint?: string;
}

function ReadOnlyRow({ label, value, hint }: ReadOnlyRowProps) {
  return (
    <div className="rounded-lg border border-border bg-muted/50 p-3 sm:p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="mt-0.5 font-mono text-sm text-foreground">{value}</p>
        </div>
      </div>
      {hint && <p className="mt-2 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function maskedEmailFor(_data: unknown): string {
  // The /accounts/me payload doesn't include the email today — User lives
  // in Identity. Show a placeholder until we expose it via auth/me.
  return "Linked to your sign-in email";
}

function FormSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-20" />
        <Skeleton className="mt-2 h-4 w-64" />
      </CardHeader>
      <CardContent className="space-y-4">
        <Skeleton className="h-14 w-full" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
        <Skeleton className="h-10 w-32" />
      </CardContent>
    </Card>
  );
}
